const GENERIC_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
]);

const IMAGE_EXTENSIONS = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
};

const VIDEO_EXTENSIONS = {
  mp4: 'video/mp4',
  m4v: 'video/m4v',
  webm: 'video/webm',
  ogg: 'video/ogg',
  ogv: 'video/ogv',
  mov: 'video/quicktime',
  flv: 'video/x-flv',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
};

const AUDIO_EXTENSIONS = {
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  oga: 'audio/ogg',
};

const MIME_EXTENSION = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'video/mp4': 'mp4',
  'video/m4v': 'm4v',
  'video/webm': 'webm',
  'video/ogg': 'ogv',
  'video/quicktime': 'mov',
  'video/x-flv': 'flv',
  'video/3gpp': '3gp',
  'video/3gpp2': '3g2',
  'video/x-msvideo': 'avi',
  'video/x-matroska': 'mkv',
  'audio/mpeg': 'mp3',
  'audio/mpeg-3': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'audio/wav': 'wav',
  'audio/ogg': 'oga',
};

const STILL_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const MESSENGER_UPLOAD_ACCEPT = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.avif',
  '.heic',
  '.heif',
  '.mp4',
  '.m4v',
  '.webm',
  '.ogg',
  '.ogv',
  '.mov',
  '.flv',
  '.3gp',
  '.3g2',
  '.avi',
  '.mkv',
].join(',');

const DEFAULT_MAX_IMAGE_EDGE = 3200;
const DEFAULT_NORMALIZE_MIN_BYTES = 6 * 1024 * 1024;
const DEFAULT_NORMALIZE_QUALITY = 0.86;

function normalizeMimeType(value = '') {
  return String(value || '').trim().toLowerCase();
}

function getExtension(name = '') {
  const clean = String(name || '').split('?')[0].split('#')[0].toLowerCase();
  const match = clean.match(/\.([a-z0-9]+)$/i);
  return match ? match[1] : '';
}

function kindFromMime(mimeType = '') {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return '';
}

function buildType(kind, mimeType, extension, source) {
  return {
    kind,
    mimeType,
    extension: extension || MIME_EXTENSION[mimeType] || '',
    source,
  };
}

function readAscii(bytes, start, end) {
  return Array.from(bytes.slice(start, end))
    .map((value) => String.fromCharCode(value))
    .join('');
}

async function readFileHeader(file, length = 32) {
  if (!file?.slice) return new Uint8Array();
  const part = file.slice(0, Math.min(length, file.size || length));
  let buffer = null;
  if (part?.arrayBuffer) {
    buffer = await part.arrayBuffer();
  } else if (typeof FileReader === 'function') {
    buffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(part);
    });
  }
  return buffer ? new Uint8Array(buffer) : new Uint8Array();
}

function inferFromHeader(bytes, fileName = '') {
  if (!bytes || bytes.length < 4) return null;

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return buildType('image', 'image/jpeg', 'jpg', 'signature');
  }

  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return buildType('image', 'image/png', 'png', 'signature');
  }

  const firstFour = readAscii(bytes, 0, 4);
  if (firstFour === 'GIF8') {
    return buildType('image', 'image/gif', 'gif', 'signature');
  }

  if (firstFour === 'RIFF') {
    const riffType = readAscii(bytes, 8, 12);
    if (riffType === 'WEBP') {
      return buildType('image', 'image/webp', 'webp', 'signature');
    }
    if (riffType === 'AVI ') {
      return buildType('video', 'video/x-msvideo', 'avi', 'signature');
    }
    if (riffType === 'WAVE') {
      return buildType('audio', 'audio/wav', 'wav', 'signature');
    }
  }

  if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
    const ext = getExtension(fileName);
    const mime = ext === 'mkv' ? 'video/x-matroska' : 'video/webm';
    return buildType('video', mime, MIME_EXTENSION[mime], 'signature');
  }

  if (firstFour === 'OggS') {
    const ext = getExtension(fileName);
    if (ext === 'oga') return buildType('audio', 'audio/ogg', 'oga', 'signature');
    return buildType('video', 'video/ogg', ext === 'ogg' ? 'ogg' : 'ogv', 'signature');
  }

  if (firstFour === 'ID3' || (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0)) {
    return buildType('audio', 'audio/mpeg', 'mp3', 'signature');
  }

  if (readAscii(bytes, 4, 8) === 'ftyp') {
    const brandString = readAscii(bytes, 8, Math.min(bytes.length, 28)).toLowerCase();
    if (/(heic|heix|hevc|hevx|heim|heis|hevm|hevs)/.test(brandString)) {
      return buildType('image', 'image/heic', 'heic', 'signature');
    }
    if (/(heif|mif1|msf1)/.test(brandString)) {
      return buildType('image', 'image/heif', 'heif', 'signature');
    }
    if (brandString.includes('qt')) {
      return buildType('video', 'video/quicktime', 'mov', 'signature');
    }
    if (/(3gp|3g2)/.test(brandString)) {
      const is3g2 = brandString.includes('3g2');
      return buildType(
        'video',
        is3g2 ? 'video/3gpp2' : 'video/3gpp',
        is3g2 ? '3g2' : '3gp',
        'signature',
      );
    }
    return buildType('video', 'video/mp4', 'mp4', 'signature');
  }

  return null;
}

function inferFromMime(file) {
  const mimeType = normalizeMimeType(file?.type);
  if (!mimeType || GENERIC_MIME_TYPES.has(mimeType)) return null;
  const kind = kindFromMime(mimeType);
  if (!kind) return null;
  return buildType(kind, mimeType, MIME_EXTENSION[mimeType] || getExtension(file.name), 'mime');
}

function inferFromExtension(file) {
  const ext = getExtension(file?.name);
  if (!ext) return null;
  const mimeType =
    IMAGE_EXTENSIONS[ext] || VIDEO_EXTENSIONS[ext] || AUDIO_EXTENSIONS[ext];
  if (!mimeType) return null;
  return buildType(kindFromMime(mimeType), mimeType, ext, 'extension');
}

export async function inferMessengerUploadType(file) {
  const header = await readFileHeader(file);
  return inferFromHeader(header, file?.name) || inferFromMime(file) || inferFromExtension(file);
}

function formatBytes(bytes) {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)}MB`;
  return `${Math.ceil(bytes / 1024)}KB`;
}

function makeFileLike(parts, name, options = {}) {
  if (typeof File === 'function') return new File(parts, name, options);
  const blob = new Blob(parts, { type: options.type });
  blob.name = name;
  blob.lastModified = options.lastModified || Date.now();
  return blob;
}

function withExtension(name = 'upload', extension = '') {
  if (!extension) return name || 'upload';
  const base = String(name || 'upload').replace(/\.[a-z0-9]+$/i, '') || 'upload';
  return `${base}.${extension}`;
}

function ensureFileType(file, inferred) {
  if (!inferred?.mimeType) return file;
  const currentType = normalizeMimeType(file.type);
  const currentExt = getExtension(file.name);
  const targetName =
    inferred.source === 'signature' && inferred.extension && currentExt !== inferred.extension
      ? withExtension(file.name, inferred.extension)
      : file.name || withExtension('upload', inferred.extension);

  if (currentType === inferred.mimeType && targetName === file.name) return file;

  return makeFileLike([file], targetName, {
    type: inferred.mimeType,
    lastModified: file.lastModified || Date.now(),
  });
}

export async function validateMessengerUploadCandidate(
  file,
  { maxImageBytes, maxVideoBytes } = {},
) {
  if (!file) throw new Error('No file selected.');
  const inferred = await inferMessengerUploadType(file);
  if (!inferred?.kind) {
    throw new Error(`${file.name || 'File'} format is not supported.`);
  }

  const limit =
    inferred.kind === 'image'
      ? maxImageBytes
      : inferred.kind === 'video' || inferred.kind === 'audio'
      ? maxVideoBytes
      : 0;

  if (limit && file.size > limit) {
    throw new Error(
      `${file.name || 'File'} is too large. Max ${formatBytes(limit)} allowed.`,
    );
  }

  return { inferred, limit };
}

function canNormalizeImage(inferred) {
  return STILL_IMAGE_MIME_TYPES.has(inferred?.mimeType);
}

function isNormalizationEnabled(flagValue = process.env.REACT_APP_MESSENGER_IMAGE_NORMALIZE) {
  return flagValue !== 'false';
}

async function loadImageElement(file) {
  if (
    typeof Image !== 'function' ||
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return null;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

async function normalizeMessengerImage(file, inferred, options = {}) {
  if (!isNormalizationEnabled(options.normalizeFlag) || !canNormalizeImage(inferred)) {
    return file;
  }

  let image;
  try {
    image = await loadImageElement(file);
  } catch {
    return file;
  }

  if (!image) return file;

  const width = image.naturalWidth || image.width || 0;
  const height = image.naturalHeight || image.height || 0;
  const maxEdge = options.maxImageEdge || DEFAULT_MAX_IMAGE_EDGE;
  const minBytes = options.normalizeMinBytes || DEFAULT_NORMALIZE_MIN_BYTES;
  const needsDownscale = Math.max(width, height) > maxEdge;
  const needsSizePass = file.size >= minBytes;

  if (!width || !height || (!needsDownscale && !needsSizePass)) return file;

  const scale = needsDownscale ? maxEdge / Math.max(width, height) : 1;
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx || typeof canvas.toBlob !== 'function') return file;

  if (inferred.mimeType === 'image/jpeg') {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const outputType = inferred.mimeType;
  const quality = outputType === 'image/png' ? undefined : options.quality || DEFAULT_NORMALIZE_QUALITY;
  const blob = await canvasToBlob(canvas, outputType, quality);
  if (!blob) return file;
  if (!needsDownscale && blob.size >= file.size) return file;

  return makeFileLike([blob], withExtension(file.name, inferred.extension), {
    type: outputType,
    lastModified: Date.now(),
  });
}

export async function prepareMessengerUploadFile(file, options = {}) {
  const { inferred } = await validateMessengerUploadCandidate(file, options);
  const typedFile = ensureFileType(file, inferred);
  const preparedFile = await normalizeMessengerImage(typedFile, inferred, options);

  return {
    file: preparedFile,
    inferred,
    normalized: preparedFile !== typedFile,
  };
}

export function getMessengerUploadErrorMessage(error) {
  const raw =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.cloudinary?.error?.message ||
    error?.cloudinary?.message ||
    error?.message ||
    'Upload failed.';

  const message = String(raw).replace(/\s+/g, ' ').trim();
  if (!message || /^request failed with status code/i.test(message)) {
    return 'Upload failed. Please try a smaller or different file.';
  }
  return message.length > 220 ? `${message.slice(0, 217)}...` : message;
}
